import Vue from 'vue'
import VueRouter from 'vue-router'
import i18n, { getCurrentLocaleOnInit } from '@/plugins/i18n.js'
import { loadTranslations } from '@/helpers/i18n.helpers'
import videoRoutes from '@/router/routes/video.routes'
import webappRoutes from '@/router/routes/webapp/routes'
import VideoLayout from '@/components/video-launcher/VideoLayout'
import BaseLayout from '@/components/layouts/BaseLayout'
import NotFoundErrorPage from '@/components/views/errors/NotFoundErrorPage'
import primesaludRoutes from '@/router/routes/opensaludprime.routes'
import opensalud4all from '@/router/routes/opensalud4all.routes'
import openlegalRoutes from '@/router/routes/openlegal.routes'
import abogalityproRoutes from '@/router/routes/abogalitypro.routes'
import healthyCommunityRoutes from '@/router/routes/webapp/healthycommunity.routes'
import healthyHabitsRoutes from '@/router/routes/webapp/healthyhabits.routes'
import store from '@/store'
import unipromelRoutes from '@/router/routes/unipromel.routes'
import publicRoutes from '@/router/routes/public.routes'
import accountRoutes from '@/router/routes/account.routes'
import routeHelpers from '@/router/routeHelpers'
import _360routes from '@/router/routes/opensalud360.routes'
import { UNIPROMEL_DOMAIN, ABOGALITYPRO_DOMAIN, OPENSALUD4ALL_DOMAIN, PRIMESALUD_DOMAIN, OPENLEGAL_DOMAIN } from '@/constants/domains'
import { COMUNIDAD_SALUDABLE_SUBDOMAIN, VIDEO_SUBDOMAIN, OPENSALUD360_SUBDOMAIN, HEALTHY_HABITS_SUBDOMAIN } from '@/constants/subdomains'
import { getPlatformName } from '@/constants/platformNames'
import { getThemeName } from '@/constants/themes'
import { ANONYMOUS_ROLE } from '@/constants/roles'
import { READ_ONLY_TOKEN_SESSION_STORAGE_KEY } from '@/constants/sessionStorage'

/* import OpensaludLanding from '@/components/public/OpensaludLanding' */

Vue.use(VueRouter)

function getRoutesFromSubdomain () {
  let [match, subdomain, domain] = /:\/\/(?:((?:\w|-)+)\.)?([^./]+)(?:(?:\.|:)(?:(?:\w|\d){2,4}))/.exec(window.location.href)
  if (domain === 'es' || domain === 'mx' || domain === 'com') { domain = subdomain }
  // Always check for full domain name or it could generate infinite loops

  const routes = [...publicRoutes, ...webappRoutes, ...[routeHelpers.buildRoutes('cuenta', accountRoutes)]]

  if (subdomain && subdomain.includes(VIDEO_SUBDOMAIN)) {
    return { layout: VideoLayout, routes: [...videoRoutes, ...routes] }
  }
  if (domain.includes(UNIPROMEL_DOMAIN)) {
    return { layout: BaseLayout, routes: [...unipromelRoutes, ...routes] }
  }
  if (domain.includes(PRIMESALUD_DOMAIN)) {
    return { layout: BaseLayout, routes: [...primesaludRoutes, ...routes] }
  }
  if (domain.includes(OPENSALUD4ALL_DOMAIN)) {
    return { layout: BaseLayout, routes: [...opensalud4all, ...routes] }
  }
  if (domain.includes(OPENLEGAL_DOMAIN)) {
    return { layout: BaseLayout, routes: [...openlegalRoutes, ...routes] }
  }
  if (domain.includes(ABOGALITYPRO_DOMAIN)) {
    return { layout: BaseLayout, routes: [...abogalityproRoutes, ...routes] }
  }
  if (subdomain && subdomain && subdomain.includes(OPENSALUD360_SUBDOMAIN)) {
    return { layout: BaseLayout, routes: [..._360routes, ...routes] }
	}
  if (subdomain && subdomain.includes(COMUNIDAD_SALUDABLE_SUBDOMAIN)) {
    return { layout: BaseLayout, routes: [...publicRoutes, ...[routeHelpers.buildRoutes('cuenta', accountRoutes)], ...healthyCommunityRoutes] }
  }
	if (subdomain && subdomain && subdomain.includes(HEALTHY_HABITS_SUBDOMAIN)) {
			return { layout: BaseLayout, routes: [...publicRoutes, ...[routeHelpers.buildRoutes('cuenta', accountRoutes)], ...healthyHabitsRoutes] }
  }
  return {
    layout: BaseLayout,
    routes: webappRoutes
  }
}

function removeParamsFromUrl () {
  const url = window.location.href
  window.location.href = url.substring(0, url.indexOf('?'))
}

const baseRoute = {
  path: '/',
  redirect: (to) => {
    return i18n.locale
  }
}

const routesFromSubdomain = getRoutesFromSubdomain()

const langBasedRoutes = {
  path: `/:lang(es|en|mx|pt)`,
  component: routesFromSubdomain.layout,
  beforeEnter: (to, from, next) => {
    const lang = getCurrentLocaleOnInit()
    if (lang !== to.params.lang) {
      return next(to.fullPath.replace(`/${to.params.lang}`, `/${lang}`))
    }
    return next()
  },
  children: [
    ...routesFromSubdomain.routes,
    ...[{
      name: 'NotFound',
      path: '(.*)',
      component: NotFoundErrorPage
    }]
  ]
}

const Router = new VueRouter({
  mode: 'history',
  scrollBehavior (to, from, savedPosition) {
    return { x: 0, y: 0 }
  },
  routes: [
    baseRoute,
    langBasedRoutes,
    {
      path: '/(.*)',
      redirect: (to) => {
        return `/${i18n.locale}${to.path}`
      }
    }
  ]
})

function goToUnauthorizedPage (to, next) {
	store.commit('setErrorPageMessage', null) 
	const returnUrl = encodeURIComponent(`${window.location.origin}${to.fullPath}`)
	next(`/error/unauthorized?returnUrl=${returnUrl}`)
}

Router.beforeEach(async (to, from, next) => {
	await store.restored
  // Remove trailing slash for SEO purposes
  if (to.path.endsWith('/')) {
    return next({path: to.path.slice(0, -1), query: to.query})
  }
  // Compute theme name based on hostname
  to.meta.theme = getThemeName()

  await loadTranslations(i18n.locale, typeof to.meta.i18n === 'string' ? [to.meta.i18n] : to.meta.i18n)

  if (!to.name || to.name.includes('Error')) {
    next()
  } else {
    if (!Vue.prototype.$apiToken && store.state.session.user) {
      store.dispatch('session/remove')
    }
    if ((to.meta.roles ? (to.meta.roles.length > 0) : null)) {
			const token = to.query.token || to.query.t || to.query.api_token || sessionStorage.getItem(READ_ONLY_TOKEN_SESSION_STORAGE_KEY) || Vue.prototype.$apiToken
      if ((to.meta.roles.includes(ANONYMOUS_ROLE) && !token) || (!to.meta.roles.includes(ANONYMOUS_ROLE) && !Vue.prototype.$apiToken)) {
				goToUnauthorizedPage(to, next)
      } else {
        if (sessionStorage.getItem(READ_ONLY_TOKEN_SESSION_STORAGE_KEY) || (to.query.t && to.query.h === '1')) {
          if (to.query.t) {
            sessionStorage.setItem(READ_ONLY_TOKEN_SESSION_STORAGE_KEY, to.query.t)
            removeParamsFromUrl()
          }
          next()
        } else {
          store.dispatch('session/create', { token }).then((response) => {
            if ((!!response && to.meta.roles.includes(ANONYMOUS_ROLE)) && (to.query.t || to.query.token || to.query.api_token)) {
              // Remove query params from URLS
              removeParamsFromUrl()
						} else if (to.meta.only360Users && !store.getters['session/userHasCompanies']) {
							store.commit('setErrorPageMessage', i18n.t('errorMessages.only-subscribed-users'))
							goToUnauthorizedPage(to, next)
            } else if ((!!response && to.meta.roles.includes(ANONYMOUS_ROLE)) || store.getters['session/isSessionRoleAllowed'](to.meta.roles)) {
              next()
            } else {
              goToUnauthorizedPage(to, next)
            }
          })
        }
      }
    } else {
      next()
    }
  }
})

Router.afterEach((to, from) => {
  // Set document.title from route meta
  if (to.meta.title && to.meta.title.text) {
    const titleText = to.meta.title.text instanceof Function ? to.meta.title.text(to) : to.meta.title.text
    document.title = `${(i18n.t(titleText) ? i18n.t(titleText) : titleText)} | ${(to.meta.tagLine || getPlatformName())}`
  }

  // Encode URI after hash if return URL is present
  const returnUrl = to.query.returnUrl || to.query.ReturnUrl
  if (returnUrl && to.hash) {
    const returnUrlEncoded = encodeURIComponent(`${to.hash}`)
    Router.replace({ query: { returnUrl: `${returnUrl}${returnUrlEncoded}` } })
  }
})

// Fix for chunk loading error
Router.onError((error) => {
  if (error.message.includes('Loading chunk') && !location.href.includes('localhost')) {
    window.location.reload()
  }
})

const { push } = VueRouter.prototype
const { isNavigationFailure, NavigationFailureType } = VueRouter

// Avoid duplicated navigation error
VueRouter.prototype.push = function (location) {
  return push.call(this, location).catch(error => {
    if (!isNavigationFailure(error, NavigationFailureType.duplicated)) {
      throw Error(error)
    }
  })
}

export const currentRoute = () => Router.currentRoute

export default Router



// WEBPACK FOOTER //
// ./src/router/index.js